import { FC } from 'react';
import { t } from 'i18next';
import { Box, Button, Typography } from '@mui/material';
import { Group, Refresh } from '@mui/icons-material';

import { IColumnDefinition } from '@common/types';
import { theme } from '@store';
import {
  CareManagementActivitiesModal,
  TableNoResultPlaceholder,
} from '@components';
import {
  PatientTableResetModal,
  PinnedColumnMenu,
  TableColumnHeightMapper,
  TableHeaderHeight,
} from 'components/TablePreferences';
import { DataGridPro, GridSortDirection } from '@mui/x-data-grid-pro';
import { useRecentPatientTable } from '@views/pages/Patients/components/RecentPatientTable/hooks';

interface ISearchPatientsTableProps {
  columns: IColumnDefinition[];
}

export const RecentPatientTable: FC<ISearchPatientsTableProps> = ({
  columns: _columns,
}) => {
  const {
    apiRef,
    pinnedColumns,
    handlePinnedColumnsChange,
    visibilityModel,
    handleUpdateVisibilityModel,
    filters,
    rows,
    columns,
    loading,
    isInitialFilters,
    isCMActivitiesModalOpen,
    handleCloseCMActivitiesModal,
    selectedPatientId,
    preferences,
    handleChangeOrder,
    handleRowClick,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handleFullReset,
    isResetViewModalOpen,
    handleOpenResetViewModal,
    handleCloseResetViewModal,
    initialSortFilters,
  } = useRecentPatientTable();

  return (
    <>
      {rows.length === 0 && !loading && isInitialFilters ? (
        <Box
          sx={{
            height: 'calc(100vh - 210px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Group sx={{ width: 56, height: 56, mb: 2 }} color='primary' />
          <Typography variant='h6'>
            {t('patients.searchPatients.noResults.title')}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('patients.searchPatients.noResults.subTitle')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box>
            <DataGridPro
              apiRef={apiRef}
              sx={{
                borderRadius: 0,
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                maxHeight: 'calc(100vh - 350px)',
                '& .MuiDataGrid-columnHeaderTitle': {
                  paddingLeft: 2.2,
                },

                '& .MuiDataGrid-cell': {
                  padding: 0,
                },
              }}
              slots={{
                columnMenu: props => (
                  <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
                ),
              }}
              slotProps={{
                row: {
                  style: {
                    cursor: 'pointer',
                  },
                },
              }}
              getRowClassName={params =>
                params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : ''
              }
              rows={rows}
              columns={columns}
              hideFooter
              loading={loading}
              onRowClick={(params, event) =>
                handleRowClick(event, params.id as string)
              }
              disableColumnSorting
              disableMultipleColumnsSorting
              disableRowSelectionOnClick
              disableColumnSelector
              sortingMode='server'
              pinnedColumns={pinnedColumns}
              onPinnedColumnsChange={handlePinnedColumnsChange}
              columnVisibilityModel={visibilityModel}
              columnHeaderHeight={TableHeaderHeight}
              getRowHeight={() =>
                TableColumnHeightMapper[preferences?.density ?? 'comfortable']
              }
              onColumnOrderChange={handleColumnOrderChange}
              onColumnWidthChange={handleColumnWidthChange}
              onColumnVisibilityModelChange={handleUpdateVisibilityModel}
              onSortModelChange={model => {
                if (
                  model[0] &&
                  (model[0].field !== filters.sort_by ||
                    model[0].sort !== filters.sort_order)
                ) {
                  handleChangeOrder(model[0].field, model[0].sort);
                } else {
                  handleChangeOrder(
                    initialSortFilters.sort_by,
                    initialSortFilters.sort_order as GridSortDirection,
                  );
                }
              }}
            />

            {rows.length === 0 && !loading && (
              <Box
                sx={{
                  bgcolor: theme.palette.common.white,
                  display: 'grid',
                  placeItems: 'center',
                  width: '100%',
                  height: 'calc(100vh - 610px)',
                  borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                }}
              >
                <TableNoResultPlaceholder />
              </Box>
            )}

            {!loading && (
              <Box
                sx={{
                  bgcolor: theme.palette.common.white,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderTop: 'none',
                  p: 0.5,
                }}
              >
                <Button
                  color='primary'
                  startIcon={<Refresh color='primary' />}
                  onClick={handleOpenResetViewModal}
                >
                  RESET VIEW
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <PatientTableResetModal
        onResetAction={handleFullReset}
        isOpen={isResetViewModalOpen}
        onClose={handleCloseResetViewModal}
      />
      <CareManagementActivitiesModal
        onClose={handleCloseCMActivitiesModal}
        isOpen={isCMActivitiesModalOpen}
        patient_id={selectedPatientId}
        isLoading={false}
        confirmAction={handleCloseCMActivitiesModal}
        variant='view'
      />
    </>
  );
};
